import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import { AppContext } from 'shared/AppContext';
import storage from 'shared/storage';
import darkTheme from '../../themes/darkTheme';
import lightTheme from '../../themes/lightTheme';

// Only apply styles to components wrapped with this provider
const LocalThemeStyles = createGlobalStyle`
  .themed-component {
    background: ${(props) => props.theme.colors.background.primary};
    color: ${(props) => props.theme.colors.text.primary};
    transition: all ${(props) =>
      props.theme.transitions?.theme || '300ms ease-in-out'};
    width: 100%;
    
    * {
      transition: inherit;
    }
  }
`;

const EnhancedThemeProvider = ({
  children,
  className = 'themed-component',
}) => {
  const context = useContext(AppContext);
  const { state } = context || { state: {} };

  const [currentTheme, setCurrentTheme] = useState(() => {
    // Check localStorage first, then AppContext, then default to dark
    const savedTheme = storage.get('theme');
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme;
    }
    return state?.theme || 'dark';
  });

  // Update theme when AppContext changes
  useEffect(() => {
    if (state?.theme && state.theme !== currentTheme) {
      setCurrentTheme(state.theme);
      storage.set('theme', state.theme);
    }
  }, [currentTheme, state.theme]);

  // Get the appropriate theme object
  const getTheme = () => {
    return currentTheme === 'dark' ? darkTheme : lightTheme;
  };

  const theme = getTheme();

  return (
    <ThemeProvider theme={theme}>
      <LocalThemeStyles />
      <div className={className}>{children}</div>
    </ThemeProvider>
  );
};

EnhancedThemeProvider.propTypes = {
  children: PropTypes.any.isRequired,
  className: PropTypes.string,
};

export default EnhancedThemeProvider;
